/**
 * useFormState - Hook for managing form state with change tracking and batch save.
 *
 * This hook provides:
 * - Tracking of original vs current property and association values
 * - Detection of changes (hasUnsavedChanges)
 * - Computation of pending changes (only changed fields)
 * - Batch save functionality using EventBuilder
 * - Reset/cancel functionality
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CRM_SAVE_TIMING, trackTiming } from '@/lib/timing';
import type { CrmObject } from '@/types/crm';

import { createCRMObjectClient } from '../core/CRMObjectClient';
import { getUserFriendlyEventError } from '../core/errors';
import { createEventBuilder } from '../core/EventBuilder';
import type { EntityTypeName } from '../core/types';
import { crmQueryKeys } from '../query/queries';
import { getObjectSeq } from '../store/crmObjectStore';
import {
  applyOptimisticBatch,
  type OptimisticBatchContext,
  rollbackOptimisticBatch,
} from '../store/optimisticBatch';
import { valuesEqual } from '../utils/valuesEqual';
import { runBackgroundSaveSubmit } from './runBackgroundSaveSubmit';

/**
 * Entities (keyed `orgId:entity_id`) with a background save currently in
 * flight, shared across every `useFormState` instance in the app.
 *
 * This guard is intentionally at module scope rather than a per-instance
 * `useRef`: on an optimistic save-and-close the slideout unmounts, and a
 * near-immediate reopen mounts a fresh hook instance whose ref would be empty
 * — letting a second save start on the same record while the first save's
 * background submit/rollback is still in flight, which could let two
 * overlapping batches roll back out of order. A module-level set preserves the
 * guard across unmount/remount so the same record can only have one save in
 * flight at a time, regardless of how many hook instances observe it.
 *
 * An entry is added synchronously the instant a save passes the in-flight
 * check — before the first `await`, so two near-simultaneous calls cannot both
 * enter — and removed on every exit path: the `catch` around the synchronous
 * pre-background path, and the background task's outer `finally` (in
 * `runBackgroundSaveSubmit`, which is why this is exported rather than
 * module-private).
 */
export const pendingSaveEntityKeys = new Set<string>();

/** Composite in-flight key so entity ids can never collide across orgs. */
function pendingSaveKey(orgId: number, entityId: string): string {
  return `${orgId}:${entityId}`;
}

/**
 * Test-only: clear the module-level in-flight registry so a save left
 * undrained by one test cannot reject a later test's first save on the same
 * `orgId:entity_id`. Mirrors `crmObjectStore.__resetForTesting`. Not for
 * production use.
 */
export function __resetPendingSavesForTesting(): void {
  pendingSaveEntityKeys.clear();
}

/** True when a changed value represents the user emptying the field. */
function isClearedValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false; // 0, false, and non-empty objects are NOT "emptied"
}

/**
 * Form values for a CRM object, including properties and associations.
 */
export interface FormValues {
  properties: Record<string, unknown>;
  associations: Record<string, string | string[] | null>;
}

/**
 * Pending changes to be saved.
 */
export interface PendingChanges {
  properties: Record<string, unknown>;
  addedAssociations: AssociationDelta[];
  removedAssociations: AssociationDelta[];
}

/**
 * One association add/remove.
 *
 * `type` is the association type name a write emits. `formKey` is the field the
 * change came from — the same string for a single-target rule, but
 * `${type}::${targetEntityTypeName}` when a kind-based rule is rendered as one
 * field per resolved target type, where several fields share one `type`.
 */
export interface AssociationDelta {
  type: string;
  formKey: string;
  targetId: string;
}

/**
 * Save result indicating whether changes were optimistically applied.
 *
 * `success: true` means the change was applied optimistically and the form
 * can close; the network submit, 409 conflict retry, and rollback-on-failure
 * all run in the background afterward, surfacing any failure via a toast
 * rather than through this return value. `success: false` occurs when either
 * the optimistic apply itself failed before anything was submitted, or a
 * prior saveChanges() call for the same entity still has a background submit
 * in flight (see `error: 'A save is already in progress for this record'`).
 */
export interface SaveResult {
  success: boolean;
  error?: string;
}

/**
 * Options for useFormState hook.
 */
export interface UseFormStateOptions {
  /** Organization ID for API calls */
  orgId: number;
  /** Current user ID for event attribution */
  userId: number;
  /** The CRM object being edited */
  object: CrmObject;
  /** Initial property values */
  initialProperties: Record<string, unknown>;
  /**
   * Initial association values, keyed by FIELD key — the association type name,
   * or `${type}::${targetEntityTypeName}` for one subset of a kind-based rule.
   */
  initialAssociations: Record<string, string | string[] | null>;
  /**
   * Maps a field key back to the association type name a write must emit.
   * Defaults to identity, which is correct whenever field keys are plain
   * association type names.
   */
  resolveAssociationTypeName?: (formKey: string) => string;
  /** Callback after successful save */
  onSaveSuccess?: () => void;
  /** Callback after save error */
  onSaveError?: (error: string) => void;
  /**
   * Per-conflict authorizations to overwrite existing single-cardinality
   * associations (user has acknowledged each conflict). Each entry scopes
   * authorization to one (entity_id, association_type_name) pair so
   * acknowledging one conflict cannot cause unintended removals on a
   * different association type for the same entity. Read via ref inside
   * saveChanges so updates do not recreate the callback.
   */
  overwriteTargets?: Array<{
    entity_id: string;
    association_type_name: string;
  }>;
  /**
   * Property keys that are required; an emptied required field is sent as ''
   * so the backend rejects the clear. Required (not optional/defaulted) so a
   * caller can't silently omit it and have a required field clear as `null`
   * (an unenforced clear) instead of `''` (a backend-rejected clear). Pass
   * an empty set explicitly when the entity type has no required fields.
   */
  requiredPropertyKeys: ReadonlySet<string>;
}

/**
 * Normalize association value to array for comparison.
 */
function normalizeAssociationValue(value: string | string[] | null): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return [...value].sort();
  }
  return [value];
}

/**
 * Resolves the freshest committed seq from three sources: the live store,
 * the React Query detail cache, and the caller-supplied object prop.
 *
 * Exported so pure-logic tests can unit-test seq resolution without
 * rendering the hook (renderHook OOMs vitest on this file). Mirrors the
 * sibling pattern in useUpdateCrmProperty.ts.
 */
export function resolveFreshSeq({
  storeSeq,
  cachedSeq,
  objectSeq,
}: {
  storeSeq: number | undefined;
  cachedSeq: number | undefined;
  objectSeq: number;
}): number {
  return Math.max(storeSeq ?? 0, cachedSeq ?? 0, objectSeq);
}

/**
 * Default `resolveAssociationTypeName`: field keys ARE association type names.
 * Module-scope so the hook's default argument is referentially stable and does
 * not re-create the association diff on every render.
 */
function identityAssociationTypeName(formKey: string): string {
  return formKey;
}

/**
 * Hook for managing form state with change tracking and batch save.
 */
export function useFormState({
  orgId,
  userId,
  object,
  initialProperties,
  initialAssociations,
  onSaveSuccess,
  onSaveError,
  overwriteTargets,
  requiredPropertyKeys,
  resolveAssociationTypeName = identityAssociationTypeName,
}: UseFormStateOptions) {
  const queryClient = useQueryClient();

  // Stable ref so saveChanges always reads the latest overwrite targets without
  // being recreated every time the user acknowledges a new conflict.
  const overwriteTargetsRef = useRef<
    Array<{ entity_id: string; association_type_name: string }>
  >(overwriteTargets ?? []);
  overwriteTargetsRef.current = overwriteTargets ?? [];

  // Original values (frozen at load time, updated after successful save)
  const [originalProperties, setOriginalProperties] =
    useState<Record<string, unknown>>(initialProperties);
  const [originalAssociations, setOriginalAssociations] =
    useState<Record<string, string | string[] | null>>(initialAssociations);

  // Current values (modified by user)
  const [currentProperties, setCurrentProperties] =
    useState<Record<string, unknown>>(initialProperties);
  const [currentAssociations, setCurrentAssociations] =
    useState<Record<string, string | string[] | null>>(initialAssociations);

  // Latest-committed mirrors of the current values, refreshed every render.
  // The save path reads these AFTER an `await` (cancelQueries) to detect a
  // same-field edit the user made DURING that await — a discrete input event
  // commits (and re-runs this render-phase assignment) before the awaited
  // promise's microtask continuation runs, so the ref reflects the newer value
  // by the time the clear loop below runs. Refs (not the closure's state)
  // because saveChanges intentionally does not depend on
  // currentProperties/currentAssociations (that would re-create it per keystroke).
  const currentPropertiesRef = useRef(currentProperties);
  currentPropertiesRef.current = currentProperties;
  const currentAssociationsRef = useRef(currentAssociations);
  currentAssociationsRef.current = currentAssociations;

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Track the object ID to detect when we're editing a different object
  const [trackedObjectId, setTrackedObjectId] = useState(object.entity_id);

  // Track which fields the user has actually touched/edited
  // This allows us to distinguish user edits from server-side changes
  // Use refs to avoid triggering effect re-runs when the Set reference changes
  const editedPropertyKeysRef = useRef<Set<string>>(new Set());
  const editedAssociationKeysRef = useRef<Set<string>>(new Set());

  // Tracks whether the component is still mounted, so the background save
  // task (which outlives the triggering render) only touches state while
  // there's a component to receive it. Rollback and the failure toast still
  // fire regardless of mount state.
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Set to true on setup so StrictMode's mount→unmount→remount cycle (and any
    // future remount) restores the mounted state instead of leaving it stuck
    // false after the first cleanup runs.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync values when viewing a different object (reset everything)
  // or when initial values change for the same object (only update originals, preserve user edits)
  useEffect(() => {
    const isNewObject = object.entity_id !== trackedObjectId;

    if (isNewObject) {
      // Different object - reset everything
      setTrackedObjectId(object.entity_id);
      setOriginalProperties(initialProperties);
      setCurrentProperties(initialProperties);
      setOriginalAssociations(initialAssociations);
      setCurrentAssociations(initialAssociations);
      editedPropertyKeysRef.current = new Set();
      editedAssociationKeysRef.current = new Set();
      setSaveError(null);
    } else {
      // Same object, initial values changed (background refetch)
      // Update original values
      setOriginalProperties(initialProperties);
      setOriginalAssociations(initialAssociations);

      // For properties/associations the user has NOT edited, update current to match new server values
      // This prevents stale server data from appearing as pending changes
      setCurrentProperties(prev => {
        const updated = { ...prev };

        // Update properties that exist in initialProperties
        for (const key of Object.keys(initialProperties)) {
          if (!editedPropertyKeysRef.current.has(key)) {
            // User hasn't edited this property, accept server's new value
            updated[key] = initialProperties[key];
          }
          // If user edited it, preserve their edit (don't overwrite)
        }

        // Remove properties that DON'T exist in initialProperties (server removed them)
        // but only if the user hasn't actively edited them
        for (const key of Object.keys(prev)) {
          if (
            !(key in initialProperties) &&
            !editedPropertyKeysRef.current.has(key)
          ) {
            Reflect.deleteProperty(updated, key);
          }
        }

        return updated;
      });

      setCurrentAssociations(prev => {
        const updated = { ...prev };
        for (const key of Object.keys(initialAssociations)) {
          if (!editedAssociationKeysRef.current.has(key)) {
            // User hasn't edited this association, accept server's new value
            updated[key] = initialAssociations[key];
          }
          // If user edited it, preserve their edit (don't overwrite)
        }
        return updated;
      });
    }
  }, [
    object.entity_id,
    initialProperties,
    initialAssociations,
    trackedObjectId,
  ]);

  // Compute pending property changes
  const pendingPropertyChanges = useMemo(() => {
    const changes: Record<string, unknown> = {};
    for (const key of Object.keys(currentProperties)) {
      if (!valuesEqual(currentProperties[key], originalProperties[key])) {
        changes[key] = currentProperties[key];
      }
    }
    return changes;
  }, [currentProperties, originalProperties]);

  // Compute pending association changes
  const pendingAssociationChanges = useMemo(() => {
    const added: AssociationDelta[] = [];
    const removed: AssociationDelta[] = [];

    for (const formKey of Object.keys(currentAssociations)) {
      // A kind-based rule is rendered as one field per resolved target type, so
      // several field keys can share one association type. The diff stays
      // per-field — which is what scopes it to the subset the user edited —
      // while the emitted event carries the real association type.
      const assocType = resolveAssociationTypeName(formKey);
      const currentIds = normalizeAssociationValue(
        currentAssociations[formKey]
      );
      const originalIds = normalizeAssociationValue(
        originalAssociations[formKey]
      );

      // Find added associations
      for (const id of currentIds) {
        if (!originalIds.includes(id)) {
          added.push({ type: assocType, formKey, targetId: id });
        }
      }

      // Find removed associations
      for (const id of originalIds) {
        if (!currentIds.includes(id)) {
          removed.push({ type: assocType, formKey, targetId: id });
        }
      }
    }

    return { added, removed };
  }, [currentAssociations, originalAssociations, resolveAssociationTypeName]);

  // Check if there are any unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const hasPropertyChanges = Object.keys(pendingPropertyChanges).length > 0;
    const hasAssociationChanges =
      pendingAssociationChanges.added.length > 0 ||
      pendingAssociationChanges.removed.length > 0;
    return hasPropertyChanges || hasAssociationChanges;
  }, [pendingPropertyChanges, pendingAssociationChanges]);

  // Count of unsaved changes (count unique fields, not individual operations)
  const changeCount = useMemo(() => {
    // Count unique changed FIELDS (not individual add/remove operations). Keyed
    // on formKey rather than association type so two subsets of one kind-based
    // rule — "Deals" and "Tasks" — count as the two edits the user made.
    const changedAssociationTypes = new Set([
      ...pendingAssociationChanges.added.map(a => a.formKey),
      ...pendingAssociationChanges.removed.map(a => a.formKey),
    ]);
    return (
      Object.keys(pendingPropertyChanges).length + changedAssociationTypes.size
    );
  }, [pendingPropertyChanges, pendingAssociationChanges]);

  // Update a single property value
  const updateProperty = useCallback(
    (key: string, value: unknown) => {
      setCurrentProperties(prev => ({
        ...prev,
        [key]: value,
      }));

      // Track edited state: mark as edited if different from original, unmark if reverted
      if (valuesEqual(value, originalProperties[key])) {
        // User reverted to original value - remove from edited set
        editedPropertyKeysRef.current.delete(key);
      } else {
        // User made a change - mark as edited
        editedPropertyKeysRef.current.add(key);
      }
      setSaveError(null);
    },
    [originalProperties]
  );

  // Update a single association value
  const updateAssociation = useCallback(
    (associationType: string, value: string | string[] | null) => {
      setCurrentAssociations(prev => ({
        ...prev,
        [associationType]: value,
      }));
      // Track edited state: mark as edited if different from original, unmark if reverted
      const currentNormalized = normalizeAssociationValue(value);
      const originalNormalized = normalizeAssociationValue(
        originalAssociations[associationType]
      );
      const isReverted =
        currentNormalized.length === originalNormalized.length &&
        currentNormalized.every(id => originalNormalized.includes(id));

      if (isReverted) {
        // User reverted to original value - remove from edited set
        editedAssociationKeysRef.current.delete(associationType);
      } else {
        // User made a change - mark as edited
        editedAssociationKeysRef.current.add(associationType);
      }
      setSaveError(null);
    },
    [originalAssociations]
  );

  // Reset all changes to original values
  const resetChanges = useCallback(() => {
    setCurrentProperties(originalProperties);
    setCurrentAssociations(originalAssociations);
    // Clear edited tracking since we're reverting to original state
    editedPropertyKeysRef.current = new Set();
    editedAssociationKeysRef.current = new Set();
    setSaveError(null);
  }, [originalProperties, originalAssociations]);

  /**
   * Save all pending changes.
   *
   * Resolves as soon as the whole event batch is applied optimistically to
   * the CRM object store — the form can close immediately. The network
   * submit, 409 conflict retry (rebuilding the batch against a fresh seq),
   * and rollback-on-terminal-failure all run afterward in the background;
   * a background failure rolls back the optimistic write, self-heals via the
   * same cache invalidation the success path performs, and surfaces via a
   * toast rather than through the returned `SaveResult`. `overwrite_targets`
   * (per-conflict association overwrite authorizations) are still attached
   * to every submit attempt.
   *
   * Returns `{ success: false }` synchronously — without applying a new
   * optimistic batch — if a prior call for the same entity still has a
   * background submit in flight. The in-flight guard clears once that prior
   * save's background task finishes (success, terminal failure, or an
   * uncaught throw), so a subsequent call is never permanently blocked.
   */
  const saveChanges = useCallback(async (): Promise<SaveResult> => {
    const saveStartTime = performance.now();

    if (!hasUnsavedChanges) {
      return { success: true };
    }

    const pendingKey = pendingSaveKey(orgId, object.entity_id);
    if (pendingSaveEntityKeys.has(pendingKey)) {
      console.warn(
        '[useFormState] Rejected overlapping saveChanges call — a save is already in flight for this entity',
        { entityId: object.entity_id }
      );
      return {
        success: false,
        error: 'A save is already in progress for this record',
      };
    }
    // Acquire the guard synchronously, before the first await below, so two
    // near-simultaneous calls can't both pass the has() check above and enter.
    // Every subsequent exit path clears it: the catch below (any synchronous
    // pre-background failure), and the background task's outer finally.
    pendingSaveEntityKeys.add(pendingKey);

    setIsSaving(true);
    setSaveError(null);

    // Everything from here until the background task launches is guarded by the
    // catch below. Any synchronous failure in this pre-background path — a
    // rejected cancelQueries, a throwing event builder or client constructor, a
    // throwing onSaveSuccess — must release the in-flight guard, or this record
    // would be permanently blocked from saving until a full reload. Declared
    // out here so the catch can roll back a write that already landed.
    let optimisticContext: OptimisticBatchContext | null = null;
    try {
      const entityTypeName = object.entity_type_name as EntityTypeName;
      const objectDetailKey = crmQueryKeys.objectDetail(
        orgId,
        entityTypeName,
        object.entity_id
      );
      // Snapshot the current association values BEFORE the await below, while
      // the refs still hold this batch's saved state. Read after the await they
      // would already reflect any edit the user made during it, defeating the
      // newer-edit check in the clear loop further down.
      const savedAssociationValues = currentAssociationsRef.current;

      await queryClient.cancelQueries({ queryKey: objectDetailKey });

      // The freshest known COMMITTED seq. Captured AFTER cancelQueries settles
      // and immediately above the optimistic apply — never re-read after it.
      // The await above yields to the event loop, during which a WebSocket
      // event can advance the store seq; capturing before the await would ship
      // a stale seq, and the optimistic write built from it would be skipped as
      // a gap even though the submit reports { success: true }. Reading here
      // (still textually above applyOptimisticBatch) closes that window while
      // preserving the invariant that the seq is read exactly once, before our
      // own bump lands — re-reading after the apply would ship seq+2 (a
      // non-recoverable 400). The detail query cache is no longer consulted
      // here (#10010 Key Decision #2): `crmObjectDetailQuery`'s queryFn now
      // upserts into the store and returns a null sentinel, so the cache never
      // holds a real seq at this key — the store is the only source.
      const initialStartSeq = resolveFreshSeq({
        storeSeq: getObjectSeq(object.entity_id),
        cachedSeq: undefined,
        objectSeq: object.last_event_seq,
      });

      const propertyChanges = pendingPropertyChanges;
      const assocChanges = pendingAssociationChanges;
      const baselineProps = originalProperties;
      const overwriteTargetsValue = overwriteTargetsRef.current;

      const buildEventsForSeq = (startSeq: number) => {
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq,
        });

        if (Object.keys(propertyChanges).length > 0) {
          const propertiesPayload: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(propertyChanges)) {
            if (isClearedValue(value)) {
              // Required cleared → '' so the backend rejects it; optional cleared → null (canonical clear).
              propertiesPayload[key] = requiredPropertyKeys.has(key)
                ? ''
                : null;
            } else {
              propertiesPayload[key] = value;
            }
          }
          builder.propertiesUpdated(
            object.entity_id,
            entityTypeName,
            propertiesPayload
          );
        }

        for (const { type, targetId } of assocChanges.added) {
          builder.associationAdded(
            object.entity_id,
            entityTypeName,
            type,
            targetId
          );
        }

        for (const { type, targetId } of assocChanges.removed) {
          builder.associationRemoved(
            object.entity_id,
            entityTypeName,
            type,
            targetId
          );
        }

        return builder.build();
      };

      const client = createCRMObjectClient(orgId, entityTypeName);

      // Build the whole batch once and apply it optimistically in full — the
      // store write matches exactly what the submit below carries (the
      // builder emits startSeq+1 and applyOptimisticBatch writes each
      // event.seq verbatim), so no manual +1 is needed.
      const events = buildEventsForSeq(initialStartSeq);
      optimisticContext =
        events.length > 0 ? applyOptimisticBatch({ events }, orgId) : null;

      // Clear edited tracking ONLY for the keys THIS save captured, and ONLY
      // when the user hasn't edited that same key AGAIN since the snapshot.
      //
      // `propertyChanges`/`assocChanges` were snapshotted from this render's
      // closure, but the `await queryClient.cancelQueries` above yields to the
      // event loop — the user can edit a field during that window. If they
      // re-edit a field this batch also saved, the store now holds a NEWER
      // unsaved value; blindly deleting the key would drop the sync effect's
      // protection and let a refetch/rollback overwrite that newer value with
      // server data (the value we saved, not what the user last typed).
      //
      // Compare each key's LIVE value (via the latest-value refs) against the
      // value this batch saved. Delete the key only when they still match (no
      // newer edit); otherwise leave it marked so the newer edit stays
      // protected. updateProperty/updateAssociation keeps re-editing keys in the
      // edited set, so a preserved key remains protected either way.
      for (const key of Object.keys(propertyChanges)) {
        if (
          valuesEqual(currentPropertiesRef.current[key], propertyChanges[key])
        ) {
          editedPropertyKeysRef.current.delete(key);
        }
      }
      const clearAssociationKeyIfUnchanged = (type: string) => {
        const live = normalizeAssociationValue(
          currentAssociationsRef.current[type] ?? null
        );
        const saved = normalizeAssociationValue(
          savedAssociationValues[type] ?? null
        );
        const unchanged =
          live.length === saved.length && live.every(id => saved.includes(id));
        if (unchanged) {
          editedAssociationKeysRef.current.delete(type);
        }
      };
      for (const { type } of assocChanges.added) {
        clearAssociationKeyIfUnchanged(type);
      }
      for (const { type } of assocChanges.removed) {
        clearAssociationKeyIfUnchanged(type);
      }
      setIsSaving(false);
      onSaveSuccess?.();
      trackTiming(CRM_SAVE_TIMING, saveStartTime, {
        entity_type: object.entity_type_name,
        // The save resolves as soon as the batch is applied optimistically —
        // the network submit/retry/rollback below runs afterward in the
        // background (see runBackgroundSaveSubmit).
        is_optimistic: true,
      });

      // Submit + 409 retry + rollback + error toast all run in the
      // background — the caller already has its result above. The in-flight
      // guard was already acquired synchronously above.
      void runBackgroundSaveSubmit({
        object,
        entityTypeName,
        orgId,
        events,
        buildEventsForSeq,
        pendingPropertyChanges: propertyChanges,
        overwriteTargetsValue,
        baselineProps,
        optimisticContext,
        objectDetailKey,
        client,
        queryClient,
        isMountedRef,
        setSaveError,
        onSaveError,
        pendingKey,
      });

      return { success: true };
    } catch (err) {
      // A synchronous failure anywhere before the background task launched
      // leaves its outer finally unreached, so release the in-flight guard here
      // or this record stays blocked until a full reload. The guard clear MUST
      // run even if the rollback itself throws — otherwise a throwing
      // rollbackOptimisticBatch would skip the delete below and permanently
      // block every later save on this record (rejected as "already in
      // progress") with no normal save error surfaced. Roll back any optimistic
      // write that already landed so the store never keeps an unsent change,
      // but do it inside try/finally and log a rollback failure separately.
      try {
        if (optimisticContext) {
          rollbackOptimisticBatch(optimisticContext);
        }
      } catch (rollbackErr) {
        console.error(
          '[useFormState] Optimistic rollback failed during save error handling',
          { entityId: object.entity_id },
          rollbackErr
        );
      } finally {
        pendingSaveEntityKeys.delete(pendingKey);
      }
      const errorMessage = getUserFriendlyEventError(err);
      setSaveError(errorMessage);
      setIsSaving(false);
      onSaveError?.(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [
    hasUnsavedChanges,
    queryClient,
    orgId,
    object,
    userId,
    pendingPropertyChanges,
    pendingAssociationChanges,
    // Note: Using pendingPropertyChanges/pendingAssociationChanges (computed via useMemo)
    // instead of currentProperties/currentAssociations to prevent callback invalidation
    // on every keystroke. The pending changes capture the delta, which is what we need.
    originalProperties,
    onSaveSuccess,
    onSaveError,
    requiredPropertyKeys,
  ]);

  return {
    // Current values
    currentProperties,
    currentAssociations,

    // State updates
    updateProperty,
    updateAssociation,

    // Change tracking
    hasUnsavedChanges,
    changeCount,
    pendingChanges: {
      properties: pendingPropertyChanges,
      addedAssociations: pendingAssociationChanges.added,
      removedAssociations: pendingAssociationChanges.removed,
    } as PendingChanges,

    // Save/reset
    saveChanges,
    resetChanges,
    isSaving,
    saveError,
  };
}
